import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

function statusColor(s) {
  if (s === 'published') return 'success';
  if (s === 'archived') return 'default';
  return 'warning';
}

export default function PlatformKnowledgeArticleDetailPage() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/knowledge-base/articles/${articleId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setArticle(json.article);
    } catch (e) {
      setError(e.message);
      setArticle(null);
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => {
    if (ready) load();
  }, [load, ready]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      const res = await platformApiFetch(`/api/platform/knowledge-base/articles/${articleId}`, { method: 'DELETE' });
      if (res.status === 204 || res.ok) {
        navigate('/platform/knowledge-base/articles');
        return;
      }
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || 'Delete failed');
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
      setDelOpen(false);
    }
  };

  if (!ready || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !article) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!article) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5">{article.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
            Global · /{article.slug}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={article.status} color={statusColor(article.status)} />
            <Chip size="small" label={article.visibility} variant="outlined" />
            <Chip size="small" label={article.category} variant="outlined" />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" component={Link} to="/platform/knowledge-base/articles">
            Back to list
          </Button>
          {isSuper && (
            <>
              <Button variant="contained" component={Link} to={`/platform/knowledge-base/articles/${articleId}/edit`}>
                Edit
              </Button>
              <Button color="error" variant="outlined" onClick={() => setDelOpen(true)}>
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {!isSuper && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only for your role. Super admins can edit or delete global articles.
        </Alert>
      )}
      {article.summary && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Summary
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {article.summary}
          </Typography>
        </Paper>
      )}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Preview
        </Typography>
        <Typography component="div" variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
          {article.content}
        </Typography>
      </Paper>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        Published: {article.published_at ? String(article.published_at) : '—'} · Updated:{' '}
        {article.updated_at ? String(article.updated_at) : '—'}
      </Typography>

      <Dialog open={delOpen} onClose={() => !deleting && setDelOpen(false)}>
        <DialogTitle>Delete global article?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove “{article.title}” ({article.slug})? Platform audit will record this deletion.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" onClick={doDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
